import React, { useCallback } from 'react';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';
import type { ISelectedHTML5File } from 'bricks/types';
import { DROP_LOCATION } from '../components/DropzoneCard/DropzoneCard';

export interface PickedFile {
  file: File;
  id: string;
  relativePath: string;
  topLevelFolder?: string;
}

export interface UseFilePickerOptions {
  onAddFiles: (files: PickedFile[]) => void;
  onCardDrop?: (files: PickedFile[]) => Promise<void>;
  onNewFolders: (folders: string[]) => void;
  onNewTopLevelFiles: (names: string[]) => void;
  currentDirCardView: number;
}

export function useFilePicker({
    onAddFiles,
    onCardDrop,
    onNewFolders,
    onNewTopLevelFiles,
    currentDirCardView,
}: UseFilePickerOptions) {
    return useCallback(
        async (
            event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
            dropLocation: string,
        ) => {
            const chosen = await getDroppedOrSelectedFiles(event);
            const indexed: PickedFile[] = chosen.map((f: ISelectedHTML5File) => {
                const relativePath = f.webkitRelativePath || f.fullPath || '';
                const parts = relativePath.split('/').filter(Boolean);
                return {
                    file: f.fileObject,
                    id: crypto.randomUUID(),
                    relativePath,
                    topLevelFolder: parts[0],
                };
            });

            // Always add to state
            onAddFiles(indexed);

            // If CARD drop, trigger upload
            if (dropLocation === DROP_LOCATION.CARD && onCardDrop) {
                // set directory to current card view
                await onCardDrop(indexed);
            }

            // Compute new folder list
            const folders = Array.from(
                new Set(indexed
                    .filter(f => f.topLevelFolder && f.topLevelFolder !== f.file.name)
                    .map(f => f.topLevelFolder!)),
            );
            onNewFolders(folders);

            // Compute top-level files
            const topFiles = indexed
                .filter(f => f.relativePath.split('/').filter(Boolean).length <= 1)
                .map(f => f.file.name);
            onNewTopLevelFiles(topFiles);

            return indexed.map(f => f.file);
        },
        [onAddFiles, onCardDrop, onNewFolders, onNewTopLevelFiles, currentDirCardView],
    );
}
